import * as React from 'react';
import ReactDOM from 'react-dom';
import './styles/index.css';
import { App } from './views/static/App';
import { FluentProvider, teamsDarkTheme } from '@fluentui/react-components';
import { createBrowserRouter, Navigate, RouterProvider } from "react-router-dom";
import { Products } from './views/lists/Products';
import { Customers } from './views/lists/Customers';
import { NavbarWrapper } from './components/NavbarWrapper';
import { GlobalStateProvider } from './state/GlobalStateProvider';
import { PrivateRoute } from './components/PrivateRoute';
import { ProductForm } from './views/forms/ProductForm';
import { PublisherForm } from './views/forms/PublisherForm';
import { CustomerForm } from './views/forms/CustomerForm';
import { Licenses } from './views/lists/Licenses';
import { LicenseForm } from './views/forms/LicenseForm';
import { WorkspaceDrawer } from './components/WorkspaceDrawer';
import { LegalDrawer } from './components/LegalDrawer';
import { Imprint } from './views/static/Imprint';
import { PrivacyPolicy } from './views/static/PrivacyPolicy';
import { CookiePolicy } from './views/static/CookiePolicy';
import { TermsAndConditions } from './views/static/TermsAndConditions';
import { DocsDrawer } from './components/DocsDrawer';
import { GeneralDocumentation } from './views/static/GeneralDocumentation';
import { KnowledgeArticleView } from './views/static/KnowledgeArticleView';
import { PublisherDocumentation } from './views/static/PublisherDocumentation';
import { CustomerDocumentation } from './views/static/CustomerDocumentation';
import { ReturnPolicy } from './views/static/ReturnPolicy';
import { PublisherPage } from './views/public/PublisherPage';
import { ProductPage } from './views/public/ProductPage';
import { ErrorBoundaryUI } from './components/ErrorBoundaryUI';
import { PublicWrapper } from './views/public/PublicWrapper';
import { TrialRequestConfirmationPage } from './views/public/TrialRequestConfirmationPage';
import { AnnouncementList } from './views/lists/AnnouncementList';
import { Checkout } from './views/static/Checkout';
import { Disclaimer } from './views/static/Disclaimer';
import { AcceptableUsePolicy } from './views/static/AcceptableUsePolicy';
import { DsarForm } from './views/public/DsarForm';

const router = createBrowserRouter([
    {
        path: "/",
        element: <NavbarWrapper/>,
        errorElement: <ErrorBoundaryUI />,
        children: [
            {
                index: true,
                element: <App />
            },
            {
                path: "checkout",
                element: <PrivateRoute />,
                children: [
                    {
                        index: true,
                        element: <Checkout />
                    }
                ]
            },
            {
                path: "docs",
                element: <DocsDrawer />,
                children: [
                    {
                        index: true,
                        element: <Navigate to="general" replace />
                    },
                    {
                        path: "general",
                        children: [
                            { index: true, element: <GeneralDocumentation /> },
                            { path: "article/:id", element: <KnowledgeArticleView /> }
                        ]
                    },
                    {
                        path: "publisher",
                        children: [
                            { index: true, element: <PublisherDocumentation /> },
                            { path: "article/:id", element: <KnowledgeArticleView /> }
                        ]
                    },
                    {
                        path: "customer",
                        children: [
                            { index: true, element: <CustomerDocumentation /> },
                            { path: "article/:id", element: <KnowledgeArticleView /> }
                        ]
                    }
                ]
            },
            {
                path: "legal",
                element: <LegalDrawer />,
                children: [
                    {
                        index: true,
                        element: <Navigate to="imprint" replace />
                    },
                    {
                        path: "imprint",
                        element: <Imprint />
                    },
                    {
                        path: "termsandconditions",
                        element: <TermsAndConditions />
                    },
                    {
                        path: "returnpolicy",
                        element: <ReturnPolicy />
                    },
                    {
                        path: "privacypolicy",
                        element: <PrivacyPolicy />
                    },
                    {
                        path: "cookiepolicy",
                        element: <CookiePolicy />
                    },
                    {
                        path: "acceptableusepolicy",
                        element: <AcceptableUsePolicy />
                    },
                    {
                        path: "disclaimer",
                        element: <Disclaimer />
                    },
                    {
                        path: "dsar",
                        element: <DsarForm />
                    }
                ]
            },
            {
                path: "public",
                element: <PublicWrapper />,
                children: [
                    {
                        path: "publishers/:publisherId",
                        element: <PublisherPage />
                    },
                    {
                        path: "publishers/:publisherId/products/:productId",
                        element: <ProductPage />
                    },
                    {
                        path: "products/:productId",
                        element: <ProductPage />
                    },
                    {
                        path: "trialRequests/:trialRequestId",
                        element: <TrialRequestConfirmationPage />
                    }
                ]
            },
            {
                path: "workspace",
                element: <PrivateRoute />,
                children: [
                    {
                        element: <WorkspaceDrawer />,
                        children: [
                            {
                                index: true,
                                element: <Navigate to="announcements" replace />
                            },
                            {
                                path: "announcements",
                                element: <AnnouncementList />
                            },
                            {
                                path: "publishers/:publisherId",
                                children: [
                                    {
                                        index: true,
                                        element: <PublisherForm />
                                    },
                                    {
                                        path: "products",
                                        children: [
                                            { index: true, element: <Products /> },
                                            { path: ":id", element: <ProductForm /> }
                                        ]
                                    },
                                    {
                                        path: "customers",
                                        children: [
                                            { index: true, element: <Customers /> },
                                            { path: ":id", element: <CustomerForm /> }
                                        ]
                                    },
                                    {
                                        path: "licenses",
                                        children: [
                                            { index: true, element: <Licenses /> },
                                            { path: ":id", element: <LicenseForm /> }
                                        ]
                                    }
                                ]
                            }
                        ]
                    }
                ]
            }
        ]
    }
]);
 
ReactDOM.render(
    <FluentProvider style={{ height: "100vh" }} theme={teamsDarkTheme}>
        <GlobalStateProvider>
            <RouterProvider router={router} />
        </GlobalStateProvider>
    </FluentProvider>,
    document.getElementById('root')
);
